import {
  toWSDateString,
  useIsMobile,
  useModalOldContext,
  WSButton,
  WSCheckboxGroup,
  WSFiltersOld,
  WSFlexBox,
  WSFormOld,
  WSIcon,
  WSInfiniteScroll,
  WSSelectOld,
  WSSelectOption,
  WSTable,
  WSTableCell,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  Deduction,
  DeductionStatus,
  DeductionStatusFilter,
  BatchStatus,
  BatchType
} from "@wingspanhq/wingspan-api-sdk";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import flatten from "lodash/flatten";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import orderBy from "lodash/orderBy";
import reduce from "lodash/reduce";
import startCase from "lodash/startCase";
import React, { useMemo } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { queriesDeductions, queriesPayeeRows, queriesBatches } from "@query";
import { WSQueries } from "../../../query/WSQuery";
import { useUrlQueryFilters } from "../../../utils/router";
import { getAllEntries } from "../../../utils/serviceHelper";
import {
  mapAndRevertParamsSortToTable,
  mapAndRevertTableSortToParams,
  SortDirection
} from "../../../utils/tableSortingHelpers";
import { CompletedDeductionDetails } from "../deductions/CompletedDuductionDetails";
import { DEDUCTION_INFO_MODAL } from "../deductions/modals/DeductionsInfo";
import { ScheduledDeductionDetails } from "../deductions/ScheduledDuductionDetails";
import styles from "./AllDeductions.module.scss";

import { selectorImportBatchName } from "../../../modules/BulkImporter/selectors/selectorImportBatchName";
import { getCommonCurrencyCode } from "../../../modules/Invoicing/utils/currency";

import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";
import { selectorSearchRowSecondaryName } from "../../../shared/selectors/selectorSearchRowSecondaryName";
import { paymentsService } from "../../../services/payments";
import { ButtonDownloadCsv } from "../../../shared/components/ButtonDownloadCsv";
import { mapDeductionsTableToCSV } from "./csvUtils";
import { useAddPayeeDeduction } from "./modals/AddPayeeDeduction";

type AllDeductionsDefaultFilters = {
  bulkBatchId?: string;
  status?: DeductionStatusFilter[];
  collaboratorEmail?: string;
  sort?: {
    name?: SortDirection;
    status?: SortDirection;
    amount?: SortDirection;
  };
};

const allDeductionsDefaultFilters = {
  bulkBatchId: undefined,
  status: undefined,
  collaboratorEmail: undefined,
  sort: undefined
};

export const AllDeductions: React.FC = () => {
  useBrowserPageTitle("All Deductions");
  const { openModal } = useModalOldContext();
  const isMobile = useIsMobile();
  const history = useHistory();
  const addPayeeDeductionModal = useAddPayeeDeduction();

  const { filters, setFilters, defaultFilters } =
    useUrlQueryFilters<AllDeductionsDefaultFilters>(
      allDeductionsDefaultFilters
    );

  const queryAllPayeeRows = queriesPayeeRows.searchPayeeRows.useQueryAll();

  // v3 `listDeductions`. Two v1 capabilities have no filter/param here and are
  // applied client-side below instead:
  //   - `sort` — the endpoint takes no sort parameter;
  //   - `labels.bulkBatchId` — no filter; v1 `labels` map to v3 `metadata`, so
  //     the batch id is matched against `Deduction.metadata.bulkBatchId`.
  // Both operate on the pages loaded so far, so they order/narrow the visible
  // rows rather than the whole collection the way the v1 server-side ones did.
  const qDeductions = queriesDeductions.listDeductions.usePaginatedQuery(
    {
      filter: {
        payeeId: filters.collaboratorEmail
          ? {
              eq: queryAllPayeeRows.flatData?.find(
                row => row.email === filters.collaboratorEmail
              )?.id
            }
          : undefined,
        status: filters.status?.length ? { anyOf: filters.status } : undefined
      }
    },
    {
      enabled: queryAllPayeeRows.isSuccess
    }
  );
  const qBulkPayableBatches = queriesBatches.listBatches.useQueryAll({
    filter: {
      type: { eq: BatchType.PAYABLE_IMPORT },
      status: { anyOf: [BatchStatus.COMPLETED, BatchStatus.FAILED] }
    }
  });

  const currencyCode = useMemo(
    () => getCommonCurrencyCode(qDeductions.flatData ?? []),
    [qDeductions.flatData]
  );

  const onNameSort = () => {
    setFilters({
      sort: { name: mapAndRevertTableSortToParams(filters.sort?.name) }
    });
  };

  const onStatusSort = () => {
    setFilters({
      sort: {
        status: mapAndRevertTableSortToParams(filters.sort?.status)
      }
    });
  };

  const onAmountSort = () => {
    setFilters({
      sort: {
        amount: mapAndRevertTableSortToParams(filters.sort?.amount)
      }
    });
  };

  const payeeOptions = useMemo((): WSSelectOption[] => {
    const payees = (queryAllPayeeRows.flatData || [])
      .filter(row => !!row.email)
      .map(row => {
        return {
          label: selectorSearchRowName(row),
          value: row.email as string,
          searchText: [
            row.email,
            row.profile?.firstName,
            row.profile?.lastName,
            row.profile?.doingBusinessAs,
            row.profile?.companyName
          ]
            .filter(Boolean)
            .join(" ")
        };
      });

    return [
      {
        label: "All",
        value: "all"
      },
      ...payees
    ];
  }, [queryAllPayeeRows.flatData]);

  return (
    <>
      <Switch>
        <Route
          path={`/member/invoices/payables/deductions/scheduled/:deductionId`}
          component={ScheduledDeductionDetails}
          exact
        />
        <Route
          path={`/member/invoices/payables/deductions/completed/:deductionId`}
          component={CompletedDeductionDetails}
          exact
        />
      </Switch>
      <WSFlexBox
        justify={"space-between"}
        direction={isMobile ? "column" : "row"}
      >
        <WSText.Heading1 mb="XL">
          All Deductions
          <WSIcon
            ml="M"
            size="XS"
            name="info-circle"
            color="gray400"
            onClick={() => openModal(DEDUCTION_INFO_MODAL)}
          />
        </WSText.Heading1>

        <WSButton.Primary
          mb="XL"
          fullWidth={isMobile}
          onClick={() => addPayeeDeductionModal.open()}
        >
          Add deduction
        </WSButton.Primary>
      </WSFlexBox>

      <WSQueries queries={{ qDeductions, queryAllPayeeRows }}>
        {({ qDeductions, queryAllPayeeRows }) => {
          // Client-side sort + bulk-batch narrowing, replacing the v1
          // server-side `sort` / `labels.bulkBatchId` (see the query above).
          const visibleDeductions = (qDeductions.flatData ?? []).filter(
            deduction =>
              filters.bulkBatchId
                ? deduction.metadata?.bulkBatchId === filters.bulkBatchId
                : true
          );
          const sortKey = (["name", "status", "amount"] as const).find(
            key => filters.sort?.[key]
          );
          const deductions = sortKey
            ? orderBy(visibleDeductions, [sortKey], [filters.sort![sortKey]!])
            : visibleDeductions;

          const tableData: WSTableItem<Deduction>[] = deductions.map(
            deduction => {
              return {
                id: deduction.id ?? "",
                data: deduction
              };
            }
          );

          const filteredBulkBatches = qBulkPayableBatches.flatData ?? [];

          return (
            <>
              <WSFlexBox alignItems="center" justify="space-between" mb="XL">
                <WSFiltersOld
                  filtersGridItemProps={{
                    span: { s: "6" }
                  }}
                  filters={filters}
                  defaultFilters={defaultFilters}
                  getCount={(defaultValues, currentValues) => {
                    const _defaultValues = omit(defaultValues, ["sort"]);
                    const _currentValues = omit(currentValues, ["sort"]);

                    return reduce<any, any>(
                      _currentValues,
                      (result, value, key) =>
                        isEqual(value, _defaultValues[key])
                          ? result
                          : result.concat(key),
                      []
                    ).length;
                  }}
                  onFilter={newFilters => {
                    setFilters(newFilters);
                  }}
                >
                  <WSFormOld.Field
                    name="status"
                    label="Status"
                    mb="2XL"
                    component={WSCheckboxGroup}
                    componentProps={{
                      options: [
                        {
                          value: DeductionStatus.COMPLETE,
                          label: "Completed"
                        },
                        {
                          value: DeductionStatus.PARTIALLY_APPLIED,
                          label: "Partially completed"
                        },
                        {
                          value: DeductionStatus.PENDING,
                          label: "Scheduled"
                        }
                      ]
                    }}
                  />

                  <WSFormOld.Field
                    name="collaboratorEmail"
                    label="Contractor"
                    mb="2XL"
                    component={WSSelectOld}
                    componentProps={{
                      searchable: true,
                      placeholder: "All contractors",
                      options: payeeOptions
                    }}
                  />

                  <WSFormOld.Field
                    name="bulkBatchId"
                    label="Bulk Batch"
                    mb="2XL"
                    component={WSSelectOld}
                    componentProps={{
                      searchable: true,
                      placeholder: "All bulk imports",
                      options: filteredBulkBatches.map(batch => ({
                        label:
                          selectorImportBatchName(batch) +
                          (batch.events?.createdAt
                            ? ` (created ${toWSDateString(batch.events.createdAt)})`
                            : ""),
                        value: batch.id as string,
                        searchText: selectorImportBatchName(batch)
                      }))
                    }}
                  />
                </WSFiltersOld>

                {tableData.length > 0 && (
                  <ButtonDownloadCsv
                    mt="M"
                    className={styles.csvLink}
                    getData={async () => {
                      // Exports what is on screen. v1 re-fetched every page
                      // from the search service with the same filter; v3
                      // `listDeductions` has no sort and no bulk-batch filter,
                      // so re-fetching would not reproduce the visible order or
                      // narrowing anyway.
                      return mapDeductionsTableToCSV(
                        deductions.map((deduction: Deduction) => {
                          const payeeRow = (
                            queryAllPayeeRows.flatData ?? []
                          ).find(row => row.id === deduction.payeeId);

                          return {
                            deduction,
                            payeeRow
                          };
                        })
                      );
                    }}
                    fileName="Deductions"
                  />
                )}
              </WSFlexBox>

              <WSInfiniteScroll
                onLoad={() => {
                  qDeductions.fetchNextPage();
                }}
                loadMore={tableData.length > 0}
                endOfList={!qDeductions.hasNextPage}
                loading={!!qDeductions.isFetchingNextPage}
              >
                <WSTable<Deduction>
                  mt="2XL"
                  tableData={tableData}
                  bottomMessage={
                    !tableData.length && (
                      <WSFlexBox.Center mt="2XL" direction="column">
                        <WSText weight="medium">No deductions found.</WSText>
                        <WSText.ParagraphSm mt="M" color="gray500">
                          To subtract an amount from a contractor's pay,
                          schedule a
                          <WSButton.Link
                            ml="XS"
                            onClick={() => addPayeeDeductionModal.open()}
                          >
                            new deduction.
                          </WSButton.Link>
                        </WSText.ParagraphSm>
                      </WSFlexBox.Center>
                    )
                  }
                  onRowClick={item => {
                    if (item.data.status === DeductionStatus.COMPLETE) {
                      history.push({
                        pathname: `/member/invoices/payables/deductions/completed/${item.data.id}`,
                        search: history.location.search
                      });
                    } else {
                      history.push({
                        pathname: `/member/invoices/payables/deductions/scheduled/${item.data.id}`,
                        search: history.location.search
                      });
                    }
                  }}
                  columns={[
                    {
                      config: {
                        gridTemplateSizeMax: "1fr",
                        hideOnScreens: ["XS", "S"],
                        header: {
                          text: "Contractor"
                        }
                      },
                      renderFunction: ({ data }) => {
                        const payeeRow = queryAllPayeeRows.flatData?.find(
                          row => row.id === data?.payeeId
                        );

                        if (!payeeRow) {
                          return null;
                        }

                        return (
                          <WSTableCell
                            text={selectorSearchRowName(payeeRow)}
                            secondaryText={selectorSearchRowSecondaryName(
                              payeeRow
                            )}
                            truncationOptions={{
                              text: { allowTwoLines: true }
                            }}
                          />
                        );
                      }
                    },
                    {
                      config: {
                        gridTemplateSizeMax: "1fr",
                        sortDirection: mapAndRevertParamsSortToTable(
                          filters.sort?.name
                        ),
                        onColumnSort: onNameSort,
                        header: {
                          text: "Name"
                        }
                      },
                      renderFunction: ({ data }) => {
                        return (
                          <WSTableCell
                            text={data.name}
                            secondaryText={data.detail}
                          />
                        );
                      }
                    },
                    {
                      config: {
                        gridTemplateSizeMax: "1fr",
                        header: {
                          text: "Status"
                        }
                      },
                      renderFunction: ({ data }) => {
                        const isComplete =
                          data.status === DeductionStatus.COMPLETE;
                        const isPartiallyApplied =
                          data.status === DeductionStatus.PARTIALLY_APPLIED;

                        return (
                          <WSTableCell
                            pill={{
                              icon: isComplete
                                ? "check-double"
                                : isPartiallyApplied
                                  ? "check"
                                  : "time",
                              theme: isComplete
                                ? "success"
                                : isPartiallyApplied
                                  ? "blue"
                                  : "neutral",
                              text: startCase(data.status)
                            }}
                          />
                        );
                      }
                    },
                    {
                      config: {
                        hideOnScreens: ["XS", "S"],
                        gridTemplateSizeMax: "1fr",
                        header: {
                          text: "Date created"
                        }
                      },
                      renderFunction: ({ data }) => (
                        <WSTableCell
                          text={toWSDateString(
                            data.events?.createdAt,
                            "mobileInput"
                          )}
                        />
                      )
                    },
                    {
                      config: {
                        hideOnScreens: ["XS", "S"],
                        gridTemplateSizeMax: "1fr",
                        header: {
                          text: `Remaining balance ${
                            currencyCode ? `(${currencyCode})` : ""
                          }`
                        }
                      },
                      renderFunction: ({ data }) => {
                        const amount = data.amount ?? 0;
                        const deductedAmount = (data.application ?? [])
                          .map(a => a.amountDeducted ?? 0)
                          .reduce((a, b) => a + b, 0);

                        const remainingAmount = deductedAmount
                          ? amount - deductedAmount
                          : amount;

                        return (
                          <WSTableCell
                            text={formatMoney(
                              remainingAmount,
                              (data.currency ?? Currency.USD) as Currency
                            )}
                          />
                        );
                      }
                    },
                    {
                      config: {
                        gridTemplateSizeMax: "1fr",
                        justify: "end",
                        sortDirection: mapAndRevertParamsSortToTable(
                          filters.sort?.amount
                        ),
                        onColumnSort: onAmountSort,
                        header: {
                          text: `Total ${
                            currencyCode ? `(${currencyCode})` : ""
                          }`
                        }
                      },
                      renderFunction: ({ data }) => {
                        return (
                          <WSTableCell
                            text={formatMoney(
                              data.amount ?? 0,
                              (data.currency ?? Currency.USD) as Currency
                            )}
                          />
                        );
                      }
                    }
                  ]}
                />
              </WSInfiniteScroll>
            </>
          );
        }}
      </WSQueries>
    </>
  );
};
